import { useMemo } from 'react';
import { parseThemeValidationContext } from '#theme';
import { mapApplicantToContext } from '../references/applicant/mapApplicantToContext.ts';
import { mapApplicantOrUserToContext } from '../references/applicant-or-user/mapApplicantOrUserToContext.ts';
import { mapAttorneyToContext } from '../references/attorney/mapAttorneyToContext.ts';
import { getSessionValidationContext } from '../references/session/getSessionValidationContext.ts';
import {
  type IEngineValidationContext,
  type IEngineValidationContextParams,
} from './IEngineValidationContext.ts';
import { parseRegistersToContext } from './parseRegistersToContext.ts';
import { parseRawSemanticFields } from './parseSemanticFields.ts';

/**
 * Funzione che ritorna il context di validazione da passare
 * al validatore.
 */
export function getEngineValidationContext(
  params: IEngineValidationContextParams,
): IEngineValidationContext {
  const themeContext = parseThemeValidationContext(params.user);

  return {
    session: {
      ...getSessionValidationContext(params.user),
      ...(themeContext.session ?? {}),
    },
    semanticFields: parseRawSemanticFields(params.semanticFields),
    semanticTags: params.semanticTags ?? null,
    dossierSemanticFields: parseRawSemanticFields(params.dossierSemanticFields),
    registers: parseRegistersToContext(params.registers),
    metadata: {
      applicantOrUser: mapApplicantOrUserToContext(
        params.user,
        params.applicant,
      ),
      applicant: mapApplicantToContext(params.applicant),
      attorney: mapAttorneyToContext(params.attorney),
      submission: {
        id: params.submission?.id,
        progressivoId: params.submission?.progressivoId,
        progressivo: params.submission?.progressivo,
      },
      submissionModule: {
        fattispecie: {
          codice: params.submissionModule?.fattispecie?.codice,
          versione: params.submissionModule?.fattispecie?.versione,
          codiceProcedimento:
            params.submissionModule?.fattispecie?.codiceProcedimento,
          versioneProcedimento:
            params.submissionModule?.fattispecie?.versioneProcedimento,
          faseSchematron: params.submissionModule?.fattispecie?.faseSchematron,
        },
        fattispecieAttivante: {
          codice: params.submissionModule?.fattispecieAttivante?.codice,
          versione: params.submissionModule?.fattispecieAttivante?.versione,
          codiceProcedimento:
            params.submissionModule?.fattispecieAttivante?.codiceProcedimento,
          versioneProcedimento:
            params.submissionModule?.fattispecieAttivante?.versioneProcedimento,
          faseSchematron:
            params.submissionModule?.fattispecieAttivante?.faseSchematron,
        },
      },
    },
    trustedEnvironment: params.trustedEnvironment ?? false,
    isIntegration: params.isIntegration ?? false,
    externalServiceContext: params.externalServiceContext,
    ...(params.others ?? {}),
  };
}

/**
 * Hook che ritorna il context di validazione da passare
 * al formhandler.
 *
 * In caso di `readonly` utilizza il contesto salvato nei dati,
 * altrimenti calcola il contesto di validazione.
 */
export function useEngineValidationContext(
  params: IEngineValidationContextParams,
): IEngineValidationContext {
  const { readonly, contextSnapshot } = params;
  // Dati specifici del tema
  const result = useMemo(() => {
    if (readonly && contextSnapshot) {
      return contextSnapshot;
    }
    // Se il contextSnapshot è undefined in modalità readonly, emetto un warning (caso legacy o errore)
    if (readonly && !contextSnapshot) {
      // biome-ignore format: <tbd>
      console.warn(`⚠️[useEngineValidationContext] Readonly con contextSnapshot undefined`);
    }
    return getEngineValidationContext(params);
  }, [contextSnapshot, params, readonly]);

  return result;
}
